/**
 * What happens between the shutter and the upload.
 *
 * A captured photo has to become three things, in an order that survives the app
 * being killed at any point:
 *
 *  1. **bytes in IndexedDB**, keyed by an `OfflineSyncId` generated here;
 *  2. **a local preview**, so the slot turns green immediately — an engineer who
 *     cannot see the photo landed will take it again;
 *  3. **a queued write**, carrying the same id, so the upload and the result
 *     line cannot be duplicated by a retry.
 *
 * Bytes first is not arbitrary. If the app dies after step 1 the photo is
 * recoverable; if it dies after a queue entry that has no bytes behind it, the
 * queue would try forever to upload a photo that does not exist.
 */

import type { PhotoRecord, PhotoStore } from '../db/local.js';
import type { Picked } from './camera.js';

export interface CaptureTarget {
  projectId: number;
  /** The breaker (or board) the photo evidences. */
  assetTag: string;
  shotCode: string;
}

export interface StoredPhoto {
  offlineSyncId: string;
  /** Object URL for the slot's thumbnail. Revoked by `releasePreview`. */
  previewUrl: string;
  capturedAt: string;
  bytes: number;
}

export interface PhotoStoreDeps {
  photos: Pick<PhotoStore, 'put'>;
  /** Injected so a test is not at the mercy of a random id or the clock. */
  newId?: () => string;
  now?: () => Date;
  makePreviewUrl?: (blob: Blob) => string;
  revokePreviewUrl?: (url: string) => void;
}

const defaultId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `off-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

export class CaptureStore {
  private readonly newId: () => string;
  private readonly now: () => Date;
  private readonly makeUrl: (blob: Blob) => string;
  private readonly revokeUrl: (url: string) => void;

  constructor(private readonly deps: PhotoStoreDeps) {
    this.newId = deps.newId ?? defaultId;
    this.now = deps.now ?? (() => new Date());
    this.makeUrl = deps.makePreviewUrl ?? ((blob) => URL.createObjectURL(blob));
    this.revokeUrl = deps.revokePreviewUrl ?? ((url) => URL.revokeObjectURL(url));
  }

  /**
   * Saves the bytes and returns what the slot needs to show.
   *
   * The id is generated here, at capture, and travels with the photo from this
   * point on — it is the only thing that makes a retry after a dropped
   * connection safe.
   */
  async keep(target: CaptureTarget, picked: Picked): Promise<StoredPhoto> {
    if (picked.blob.size === 0) {
      // Would tick the slot green with nothing behind it.
      throw new Error('refusing to store an empty photo');
    }

    const offlineSyncId = this.newId();
    const capturedAt = this.now().toISOString();
    const record: PhotoRecord = {
      offlineSyncId,
      projectId: target.projectId,
      assetTag: target.assetTag,
      shotCode: target.shotCode,
      blob: picked.blob,
      capturedAt,
    };
    await this.deps.photos.put(record);

    // Only after the bytes are safe: a preview for a photo that was not stored
    // is a green slot that survives nothing.
    return {
      offlineSyncId,
      previewUrl: this.makeUrl(picked.blob),
      capturedAt,
      bytes: picked.blob.size,
    };
  }

  /**
   * Frees a preview.
   *
   * Object URLs pin the whole blob in memory until revoked. Fifty-two of them on
   * one breaker page is the difference between a page that scrolls and a tab iOS
   * kills.
   */
  releasePreview(url: string | undefined): void {
    if (url && url.startsWith('blob:')) this.revokeUrl(url);
  }
}

/** The result-line fields a stored photo contributes. Shared so the queue and the report agree. */
export function photoFields(target: CaptureTarget, stored: StoredPhoto): Record<string, unknown> {
  return {
    AssetTag: target.assetTag,
    ShotCode: target.shotCode,
    CapturedAt: stored.capturedAt,
    OfflineSyncId: stored.offlineSyncId,
  };
}
